import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetFarms, dataSetContactInfos, dataSetAccreditations, dataSetFtpDetails } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { IFarm } from '../../../@types/model/masterData/farm/farm';
import { IFarmFormValues } from '../../../@types/model/masterData/farm/farmFormValues';
import FarmHttpService from '../../../services/http/masterData/farmHttpService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import FarmForm from './form/FarmForm';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import { IApiError } from '../../../@types/other';
import ContactInfoHttpService from '../../../services/http/masterData/contactInfoHttpService';
import AccreditationHttpService from '../../../services/http/masterData/accreditationHttpService';
import FtpDetailHttpService from '../../../services/http/masterData/ftpDetailHttpService';
import { setFarmMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { useLocation } from 'react-router-dom';
import FarmModelHelper from '../../../@types/model/masterData/farm/farmModelHelper';
import UserHelper from '../../../services/helper/userHelper';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';

const FarmScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const farms = useAppSelector(x => x.masterData.farms);
    const organizations = useAppSelector(x => x.masterData.organizations);
    const ftpDetails = useAppSelector(x => x.masterData.ftpDetails);
    const contactInfos = useAppSelector(x => x.masterData.contactInfos);
    const accreditations = useAppSelector(x => x.masterData.accreditations);
    const selectedOrganizationIds = useAppSelector(x => x.data.selectedOrganizationIds);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedFarm, setSelectedFarm] = useState<IFarm | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [deletingFarm, setDeletingFarm] = useState<IFarm | null>(null);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, [selectedOrganizationIds]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const res = await FarmHttpService.getFarmData(selectedOrganizationIds);
                const res2 = await ContactInfoHttpService.getContactInfoData();
                const res3 = await AccreditationHttpService.getAccreditationData();
                const res4 = await FtpDetailHttpService.getFtpDetailData();

                dispatch(dataSetFarms(res.data));
                dispatch(dataSetContactInfos(res2.data));
                dispatch(dataSetAccreditations(res3.data));
                dispatch(dataSetFtpDetails(res4.data));
            } catch (error : unknown) {
                const apiError = error as IApiError | undefined;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading farms.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const onSubmit = async (values : IFarmFormValues) => {
        setIsLoading(true);

        try {
            const upsert = FarmModelHelper.createUpsert(values);
            const res = await FarmHttpService.addOrUpdateFarm(upsert);
            const updatedList = upsertArrayElement(farms, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetFarms(updatedList));
            await setFarmMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedFarm ? 'Farm updated successfully.' : 'Farm added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating farm data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeFarm = async () => {
        if (!deletingFarm) return;
        const updatedFarm = { ...deletingFarm, isActive: false };
        setIsLoading(true);
        try {
            const res = await FarmHttpService.deleteFarm(updatedFarm.id);
            const updatedList = upsertArrayElement(farms, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetFarms(updatedList));
            await setFarmMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Farm deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting farm.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedFarm(null);
    };

    const edit = (selectedRow : IFarm) => {
        setSelectedFarm(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : IFarm) : void => {
        setDeletingFarm(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingFarm(null);
    };

    const deleteButtonTooltip = (row : IFarm) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Farm', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    const formatOrganizations = (organizationIds : Array<number>) => {
        return organizations
            .filter(x => organizationIds.some(y => y === x.id))
            .map(x => x.code)
            .join(', ');
    };

    const formatFtpDetails = (ftpDetailIds : Array<number>) => {
        return ftpDetails
            .filter(x => ftpDetailIds.some(y => y === x.id))
            .map(x => x.name)
            .join(', ');
    };

    const formatContactInfos = (contactInfoIds : Array<number>) => {
        return contactInfos
            .filter(x => contactInfoIds.some(y => y === x.id))
            .map(x => x.name)
            .join(', ');
    };

    const getAccreditationName = (accreditationId : number) => {
        const accreditation = accreditations.find(x => x.id === accreditationId);
        return accreditation?.name ?? '';
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    const initialValues = useMemo<IFarmFormValues>(() => {
        return FarmModelHelper.createFormValues(
            selectedFarm,
            organizations,
            ftpDetails,
            contactInfos,
            accreditations
        );
    }, [
        selectedFarm,
        organizations,
        ftpDetails,
        contactInfos,
        accreditations,
        isDialogOpen
    ]);

    const filteredRows = useMemo(() => {
        return farms.filter(x => x.isActive && selectedOrganizationIds?.some(y => x.organizationIds.includes(y)));
    }, [farms, selectedOrganizationIds]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            <Card className='fdc hfill'>
                <CustomTable<IFarm>
                    
                    enableAdding
                    // addTooltipRightValidation={!userHasRights.addRight}
                    disableAddButton={!userHasRights.addRight || isLoading}
                    addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('Farm', RightGrantsEnum.Add) : 'Add'}
                    addFunction={openModal}
                    // addTooltipOnClick={() : void => requestAccess('Add')}
                
                    enableEditing
                    // editTooltipRightValidation={!userHasRights.editRight}
                    disableEditButton={!userHasRights.editRight || isLoading}
                    editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Farm', RightGrantsEnum.Edit) : 'Edit'}
                    editFunction={edit}
                    // editTooltipOnClick={() : void => requestAccess('Edit')}

                    enableDeleting
                    // deleteTooltipRightValidation={!userHasRights.deleteRight}
                    disableDeleteButton={(row : IFarm) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                    deleteTooltip={deleteButtonTooltip}
                    deleteFunction={onDelete}
                    // deleteTooltipOnClick={() : void => requestAccess('Delete')}

                    enableSorting
                    enableFiltering
                    enablePagination
                    columns={[
                        { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                        { title: 'Name', field: 'name', width: 250, enableFiltering: true, enableSorting: true },
                        { title: 'Exported Code', field: 'exportedCode', enableFiltering: true, enableSorting: true },
                        { title: 'Exported Desc', field: 'exportedDescription', enableFiltering: true, enableSorting: true },
                        { title: 'Accreditation', field: 'accreditationId', formatFunction: getAccreditationName, enableFiltering: true, enableSorting: true },
                        { title: 'Organizations', field: 'organizationIds', formatFunction: formatOrganizations, width: 250, enableFiltering: true, enableSorting: true },
                        { title: 'FtpDetails', field: 'ftpDetailIds', formatFunction: formatFtpDetails, width: 250, enableFiltering: true, enableSorting: true },
                        { title: 'Contact Info', field: 'contactInfoIds', formatFunction: formatContactInfos, width: 250, enableFiltering: true, enableSorting: true },
                        { title: 'Global Gap Nr.', field: 'globalGapNumber', enableFiltering: true, enableSorting: true },
                        { title: 'Bank Name', field: 'bankName', enableFiltering: true, enableSorting: true },
                        { title: 'Bank Account Nr.', field: 'bankAccount', enableFiltering: true, enableSorting: true },
                        { title: 'VAT', field: 'vat', enableFiltering: true, enableSorting: true },
                        { title: 'Telephone Nr.', field: 'telNumber', enableFiltering: true, enableSorting: true },
                        { title: 'Fax Nr.', field: 'faxNumber', enableFiltering: true, enableSorting: true },
                        { title: 'Extended Description', field: 'extendedDescription', width: 400, enableFiltering: true, enableSorting: true },
                        { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                        { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                        { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                    ]}
                    rows={filteredRows}
                    initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                    pageSizes={[50, 150, 250, 500, 1000]}
                    pageHeight={190}
                    isActive={row => row.isActive}
                />
            </Card>
            {isDialogOpen &&
                <ProjectDialog
                    title={selectedFarm ? 'Edit Farm' : 'Add Farm'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'md'}
                    fullWidth
                >
                    <FarmForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isLoading={isLoading}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Farm'}
                open={!!deletingFarm}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeFarm}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default FarmScreen;
